import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, CalendarClock, ChevronDown, Lock, Play } from "lucide-react";
import client from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

// The story list a family sees (same rows, same covers and tones), for staff who are not learners themselves:
//   - a teacher: stories their class has reached open normally; the rest are shown greyed out and cannot be opened;
//   - a principal: every story is shown greyed out (they manage the school, they do not take the stories).
// A locked row is a plain block with no link, so there is nothing to click through to.

const COVER = (seq) => (seq >= 1 && seq <= 21 ? `/covers/story-${seq}-art.webp` : null);
const TONES = ["hv-tile-teal", "hv-tile-rose", "hv-tile-gold", "hv-tile-lavender"];

export default function ClassStories({ heading = "Stories", reached = 0, current = null, started = false, allLocked = false, note = null }) {
  const { getLoc } = useLang();
  const [juz, setJuz] = useState(null);
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);

  // Open on the Juz that holds the class's current story (Juz 1 for a class that has not started, or a principal).
  useEffect(() => {
    if (juz !== null) return;
    const seq = !allLocked && started ? current || reached : 0;
    if (!seq) { setJuz(1); return; }
    client.get(`/stories/${seq}`).then(({ data }) => setJuz(data.story?.juz_number || 1)).catch(() => setJuz(1));
  }, [juz, current, reached, started, allLocked]);

  useEffect(() => {
    if (juz === null) return;
    setLoading(true);
    client.get(`/stories?juz=${juz}&limit=40&slim=1`)
      .then(({ data }) => setStories((data.stories || []).sort((a, b) => a.global_sequence - b.global_sequence)))
      .catch(() => setStories([]))
      .finally(() => setLoading(false));
  }, [juz]);

  return (
    <section className="mt-10" data-testid="class-stories">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="hv-heading text-3xl flex items-center gap-3">
          <BookOpen size={26} strokeWidth={2.5} className="text-hv-teal" /> {heading}
        </h2>
        <label className="flex items-center gap-2 relative">
          <span className="text-[11px] font-bold uppercase tracking-wider text-hv-slate">Juz</span>
          <select value={juz || 1} onChange={(e) => setJuz(Number(e.target.value))} data-testid="home-juz-select"
                  className="appearance-none rounded-full bg-white border-2 border-[#CFE3DC] ps-5 pe-10 py-2 font-extrabold text-hv-navy focus:outline-none focus:border-hv-teal cursor-pointer">
            {Array.from({ length: 30 }, (_, i) => i + 1).map((j) => <option key={j} value={j}>{j}</option>)}
          </select>
          <ChevronDown size={16} strokeWidth={2.6} className="pointer-events-none absolute end-3.5 text-hv-navy" />
        </label>
      </div>
      {note && <p className="mt-2 text-hv-slate">{note}</p>}

      {loading ? (
        <p className="mt-4 text-hv-slate">Loading…</p>
      ) : stories.length === 0 ? (
        <p className="mt-4 text-hv-slate">No stories in this Juz yet.</p>
      ) : (
        <div className="mt-6 grid sm:grid-cols-2 gap-5">
          {stories.map((s) => {
            const open = !!s.unlocked || (!allLocked && started && s.global_sequence <= reached);   // a principal opens only what the server says is open to them (the demo's first four)
            const isCurrent = open && s.global_sequence === current;
            const cover = COVER(s.global_sequence) || s.storybook?.[0]?.image_url || null;
            const tone = TONES[(s.global_sequence - 1) % TONES.length];
            const title = getLoc(s.title) || `Story ${s.global_sequence}`;
            const inner = (
              <>
                <div className={`w-[130px] h-[110px] sm:w-[170px] sm:h-[135px] rounded-2xl overflow-hidden shrink-0 bg-white/40 flex items-center justify-center`}>
                  {cover
                    ? <img src={cover} alt="" loading="lazy" className={`w-full h-full object-cover ${open ? "" : "grayscale"}`} />
                    : <div className="hv-icon-circle w-14 h-14">{open ? <Play size={22} strokeWidth={2.5} /> : <Lock size={22} strokeWidth={2.5} />}</div>}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-bold text-hv-teal">#{s.global_sequence} · Juz {s.juz_number}{isCurrent ? " · this week" : ""}</div>
                  <div className="hv-heading text-hv-navy text-base sm:text-lg leading-snug mt-1 line-clamp-2">{title}</div>
                  {open ? (
                    <div className="mt-2 inline-flex items-center gap-1.5 text-xs sm:text-sm font-bold text-hv-teal">
                      <Play size={12} fill="currentColor" strokeWidth={0} /> Open story
                    </div>
                  ) : (
                    <div className="mt-2 inline-flex items-center gap-1.5 text-xs sm:text-sm font-bold text-hv-slate">
                      <CalendarClock size={14} strokeWidth={2.5} className="text-hv-teal" />
                      {allLocked ? "Locked" : "Unlocks as the class reaches it"}
                    </div>
                  )}
                </div>
                <span className={`absolute top-3 end-3 w-7 h-7 rounded-full shadow flex items-center justify-center text-xs font-bold shrink-0 bg-white text-hv-navy`}>
                  {open ? s.global_sequence : <Lock size={12} strokeWidth={3} />}
                </span>
              </>
            );
            return open ? (
              <Link key={s.global_sequence} to={`/story/${s.global_sequence}`} data-testid={`class-story-${s.global_sequence}`}
                    className={`relative flex items-center gap-4 rounded-[1.75rem] p-3 pe-5 shadow-sm transition-all hover:shadow-md hover:-translate-y-0.5 ${tone} ${isCurrent ? "ring-2 ring-hv-teal" : ""}`}>
                {inner}
              </Link>
            ) : (
              <div key={s.global_sequence} data-testid={`class-story-locked-${s.global_sequence}`} aria-disabled="true"
                   className={`relative flex items-center gap-4 rounded-[1.75rem] p-3 pe-5 shadow-sm opacity-60 grayscale cursor-not-allowed select-none ${tone}`}>
                {inner}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
